import { describeExportCoverage } from '../audio/exportCoverage';
import { useEffect, useRef } from 'react';
import { flushSync } from 'react-dom';
import { activateAudioContext } from '../audio/audioContextActivation';
import { useFx } from '../contexts/FxContext';
import { useAudio } from '../contexts/AudioContext';
import { useDrum } from '../contexts/DrumContext';
import { useInstrument } from '../contexts/InstrumentContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { transportClock } from '../audio/transportClock';
import { schedulerClockMode } from '../audio/schedulerClock';

// No studio UI, sockets, autosave or keyboard shortcuts. The visible MCP card
// controls this instance; synthesis, routing and scheduling use studio providers.
export default function ChatPlaybackEngine() {
  const audio = useAudio();
  const fx = useFx();
  const drum = useDrum();
  const instrument = useInstrument();
  const { setLocalMode } = useWebSocket();
  const current = useRef();
  current.current = { audio, drum, instrument, fx };
  useEffect(() => { setLocalMode(true); }, [setLocalMode]);
  useEffect(() => {
    if (!audio.audioContext) return undefined;
    const events = new EventTarget();
    let loaded = false;
    let loading = null;
    let requestedPayload = null;
    let preparation = 'idle';
    let unlockWaiter = null;
    let rendering = null;
    let bpm = 120;
    let duration = 0;
    let playing = false;
    let enabled = !window.__COOKUP_CHAT_RETIRED__;
    let disposed = false;
    const checkActive = () => { if (disposed || window.__COOKUP_CHAT_RETIRED__) throw new Error('This CookUp panel was replaced by a newer one.'); };
    const setPreparation = phase => { preparation = phase; events.dispatchEvent(new Event('preparationchange')); };
    const prepareStage = async (phase, work) => {
      setPreparation(phase);
      let timer;
      try {
        return await Promise.race([Promise.resolve().then(work), new Promise((_, reject) => {
          timer = setTimeout(() => reject(new Error('Loading timed out. Tap Play to retry.')), 60000);
        })]);
      } finally { clearTimeout(timer); }
    };
    const context = audio.audioContext;
    const onContextState = () => {
      if (context.state === 'running' && unlockWaiter) { unlockWaiter.resolve(); unlockWaiter = null; }
      if (context.state !== 'running' && playing) {
        playing = false;
        current.current.audio.pause();
        events.dispatchEvent(new Event('pause'));
      }
    };
    context.addEventListener('statechange', onContextState);
    const activate = async () => {
      checkActive();
      enabled = true;
      if (!await activateAudioContext(context)) throw new Error('Audio is blocked on this device. Tap Play again to enable it.');
      onContextState();
    };
    const engine = {
      async deactivate() {
        enabled = false;
        playing = false;
        current.current.audio.stop();
        events.dispatchEvent(new Event('pause'));
        if (rendering) await rendering.catch(() => {});
        if (!enabled) await current.current.audio.audioContext.suspend();
      },
      get previewCanStart() { return current.current.audio.audioContext.state === 'running'; },
      get audioState() { return context.state; },
      get preparationState() { return preparation; },
      async activatePreview() {
        // Use the website's iOS unlock (silent source + bounded resume) directly
        // in the tap, before version checks, downloads or instrument startup.
        await activate();
      },
      get paused() { return !playing; },
      get ended() { return duration > 0 && this.currentTime >= duration; },
      get currentTime() {
        const beat = playing && current.current.audio.getAudiblePlayheadBeat
          ? current.current.audio.getAudiblePlayheadBeat() : transportClock.getBeat();
        return beat * 60 / bpm;
      },
      set currentTime(seconds) { current.current.audio.seekTo(Math.max(0, Number(seconds) || 0) * bpm / 60); events.dispatchEvent(new Event('seeked')); },
      get volume() { return current.current.audio.masterVolume; },
      set volume(value) { current.current.audio.setMasterVolume(value); },
      addEventListener(...args) { events.addEventListener(...args); },
      removeEventListener(...args) { events.removeEventListener(...args); },
      syncTransport(value) {
        if (!value && playing) { playing = false; events.dispatchEvent(new Event('pause')); }
      },
      async loadSession(payload) {
        checkActive();
        enabled = true;
        if (rendering) await rendering.catch(() => {});
        if (loading) await loading.catch(() => {});
        requestedPayload = payload;
        loaded = false;
        loading = (async () => {
          // WKWebView can withhold worklet/decoder startup until a gesture. Do
          // not spend the preparation timeout waiting for the user to tap Play.
          if (context.state !== 'running') {
            setPreparation('waiting-for-tap');
            await new Promise((resolve, reject) => { unlockWaiter = { resolve, reject }; onContextState(); });
          }
          checkActive();
          current.current.audio.stop();
          playing = false;
          window.__COOKUP_CHAT_ASSETS__ = payload.assets || {};
          window.__COOKUP_CHAT_TABLES__ = payload.tables || {};
          bpm = payload.state.bpm || 120;
          duration = Math.max(0, ...(payload.state.timelineClips || []).map(clip => clip.startBeat + clip.durationBeats)) * 60 / bpm;
          current.current.audio.setBpm(bpm, { scaleAudioClips: false });
          checkActive();
          await prepareStage('loading-drums', () => current.current.drum.loadPatternFromSession(payload.state));
          checkActive();
          let timelineLoad;
          // The engine is hidden inside a host card. Safari may pause its
          // animation frames; commit provider updates without waiting for paint.
          await prepareStage('loading-tracks', () => {
            flushSync(() => { timelineLoad = current.current.audio.loadTimelineFromSession(payload.state); });
            return timelineLoad;
          });
          flushSync(() => {});
          await prepareStage('loading-instruments', () => current.current.instrument.waitForPlaybackReady());
          await prepareStage('loading-effects', () => current.current.fx.waitForPlaybackReady(payload.state));
          if (current.current.audio.readSessionState().tracks.some(track => track.error)) throw new Error('Some session audio could not be loaded. Refresh and retry.');
          checkActive();
          loaded = true;
          setPreparation('ready');
        })().catch(error => {
          const stage = preparation;
          setPreparation('error');
          throw new Error(`${stage === 'loading-drums' ? 'Drum samples' : stage === 'loading-instruments' ? 'Instruments' : stage === 'loading-effects' ? 'Effects' : 'Session audio'}: ${error.message || 'could not load'}`);
        });
        try { await loading; } finally { loading = null; }
      },
      async renderPreview(job) {
        checkActive();
        if (rendering) return rendering;
        rendering = (async () => {
          if (loading) await loading;
          if (!loaded) throw new Error('Preview instruments are not ready. Refresh and retry.');
          await current.current.instrument.waitForPlaybackReady();
          checkActive();
          const state = current.current.audio.readSessionState();
          await current.current.fx.waitForPlaybackReady(state);
          const coverage = describeExportCoverage({ ...state, trackFxMap: Object.fromEntries(
            [...state.tracks, ...state.buses].map(track => [track.id, track.fx || []])
          ) });
          const blob = await current.current.audio.exportMix({ returnBlob: true, bitDepth: 16,
            // A download gets the -1 dBTP safety ceiling live playback has; a preview stays raw so a review hears real headroom.
            maxSeconds: job.maxSeconds, fullSessionExport: !!job.exportFormat, applyMasterLimiter: !!job.exportFormat,
            windowStartBeat: job.exportFormat ? 0 : Number(job.startBeat) || 0 });
          checkActive();
          if (!blob || blob.size > (job.maxBytes || 5 * 1024 * 1024)) throw new Error('Audio preview is empty or too large.');
          const url = new URL(job.uploadUrl);
          if (url.pathname !== '/mcp/audio-review' || url.username || url.password
            || !(url.protocol === 'https:' || (url.protocol === 'http:' && ['localhost', '127.0.0.1'].includes(url.hostname)))) {
            throw new Error('Invalid audio preview destination.');
          }
          const response = await fetch(url.href, { method: 'POST', credentials: 'omit',
            headers: { 'Content-Type': 'audio/wav', Authorization: `Bearer ${job.ticket}`,
              'X-CookUp-Render-Coverage': coverage.complete ? 'complete' : 'partial' },
            body: blob, signal: AbortSignal.timeout(job.exportFormat ? 240000 : 60000) });
          const result = await response.json();
          if (!response.ok || !result.ready) throw new Error(result.error || 'Audio preview upload failed.');
          return result;
        })();
        try { return await rendering; } finally { rendering = null; }
      },
      async play() {
        checkActive();
        enabled = true;
        // Resume synchronously with the user gesture, before any async loads.
        await activate();
        while (loading) await loading;
        if (!loaded && requestedPayload) await engine.loadSession(requestedPayload);
        if (!loaded) throw new Error('Live audio is still loading. Refresh and retry.');
        await current.current.fx.waitForPlaybackReady(current.current.audio.readSessionState());
        const started = await current.current.audio.play();
        if (!started) throw new Error('Playback could not start. Press Play to retry.');
        playing = current.current.audio.audioContext.state === 'running';
        current.current.audio.startAudioPerformanceProbe();
        events.dispatchEvent(new Event('play'));
      },
      // How well the scheduler kept up since Play: the evidence for whether a
      // stutter came from late ticks, an emptied window or the device itself.
      readTiming() {
        const context = current.current.audio.audioContext;
        const probe = current.current.audio.finishAudioPerformanceProbe();
        if (playing) current.current.audio.startAudioPerformanceProbe();
        if (!probe.schedulerTicks) return null;
        return { ...probe, clock: schedulerClockMode(),
          outputLatencyMs: Math.round(((context.baseLatency || 0) + (context.outputLatency || 0)) * 1000) };
      },
      pause() { playing = false; current.current.audio.pause(); events.dispatchEvent(new Event('pause')); }
    };
    window.cookupLiveAudio = engine;
    window.dispatchEvent(new Event('cookup:engine-ready'));
    return () => {
      disposed = true; enabled = false; loaded = false;
      context.removeEventListener('statechange', onContextState);
      if (unlockWaiter) { unlockWaiter.reject(new Error('This CookUp panel was closed.')); unlockWaiter = null; }
      current.current.audio.stop(); delete window.cookupLiveAudio; delete window.__COOKUP_CHAT_ASSETS__; delete window.__COOKUP_CHAT_TABLES__;
    };
  }, [audio.audioContext]);
  useEffect(() => { window.cookupLiveAudio?.syncTransport(audio.isPlaying); }, [audio.isPlaying]);
  return null;
}
